import {
  Component,
  EventEmitter,
  Output,
  Input,
  OnInit,
  Inject,
  PLATFORM_ID,
  NgZone,
  OnDestroy,
} from "@angular/core";
import { CommonModule, isPlatformBrowser, DOCUMENT } from "@angular/common";
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from "@angular/forms";
import { AuthService } from "../../services/auth.service";
import { User } from "@supabase/supabase-js";
import { Router, RouterLink } from "@angular/router";
import { ModalComponent } from "../../shared/components/modal/modal.component";
import { environment } from "../../../environments/environment";
import { LoginCarouselComponent } from "../../components/login-carousel/login-carousel.component";

interface Window {
  turnstile?: {
    render: (idOrContainer: string | HTMLElement, options: any) => string;
    reset: (widgetIdOrContainer: string | HTMLElement) => void;
    getResponse: (
      widgetIdOrContainer: string | HTMLElement
    ) => string | undefined;
    remove: (widgetIdOrContainer: string | HTMLElement) => void;
    execute?: (idOrContainer?: string | HTMLElement) => void;
  };
}

declare var window: Window;

@Component({
  selector: "app-login",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ModalComponent,
    RouterLink,
    LoginCarouselComponent,
  ],
  templateUrl: "./login.component.html",
})
export class LoginComponent implements OnInit, OnDestroy {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() loginComplete = new EventEmitter<User>();
  @Input() initialView: "login" | "register" = "login";
  
  isSignUpMode = false;

  private turnstileScriptLoaded = false;
  private turnstileWidgetId: string | null = null;
  private isRenderingTurnstile = false;
  private turnstileTokenPromise: Promise<string | null> | null = null;
  private turnstileTokenResolve: ((token: string | null) => void) | null = null;

  emailForm: FormGroup;
  verificationForm: FormGroup;

  isSubmittingEmail = false;
  isVerifyingCode = false;
  showVerificationForm = false;
  codeSent = false;
  currentStep = 1;
  isSubmittingGoogle = false;
  isResendingCode = false;
  lastOtpSendTimestamp: number | null = null;
  readonly resendCooldownSeconds = 60; // Changed from 30 back to 60
  resendCooldownActive = false;
  resendTimerDisplay: string | null = null;
  private resendTimerInterval: any;
  showResendCooldownOnButton = false;

  errorMessage: string | null = null;
  successMessage: string | null = null;

  turnstileSiteKey = environment.turnstileSiteKey;
  turnstileToken: string | null = null;

  isSubmitting = false;
  formError = "";

  constructor(
    private fb: FormBuilder,
    public authService: AuthService,
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
    private ngZone: NgZone
  ) {
    this.emailForm = this.fb.group({
      email: ["", [Validators.required, Validators.email]],
    });
    this.verificationForm = this.fb.group({
      code: [
        "",
        [Validators.required, Validators.minLength(6), Validators.maxLength(6)],
      ],
    });
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.loadTurnstileScript();
    }
  }

  private loadTurnstileScript(): void {
    if (this.document.getElementById("cf-turnstile-script")) {
      this.turnstileScriptLoaded = true;
      return;
    }
    const script = this.document.createElement("script");
    script.id = "cf-turnstile-script";
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.ngZone.run(() => {
        this.turnstileScriptLoaded = true;
      });
    };
    script.onerror = () => {
      this.formError =
        "Could not load security feature. Please check your connection or ad-blocker.";
      this.turnstileScriptLoaded = false;
    };
    this.document.head.appendChild(script);
  }

  private async renderTurnstileWidgetIfNotExists(): Promise<boolean> {
    if (this.turnstileWidgetId) {
      return true;
    }
    if (
      !this.turnstileScriptLoaded ||
      typeof window.turnstile === "undefined"
    ) {
      this.formError =
        "Security features are not ready. Please wait a moment and try again, or refresh the page.";
      if (!this.turnstileScriptLoaded) this.loadTurnstileScript();
      return false;
    }
    if (this.isRenderingTurnstile) {
      let attempts = 0;
      while (this.isRenderingTurnstile && attempts < 30) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts++;
      }
      if (!this.turnstileWidgetId) {
        this.formError = "Security feature initialization timed out.";
        return false;
      }
      return true;
    }

    this.isRenderingTurnstile = true;

    const turnstileContainer = this.document.getElementById("cf-turnstile");
    if (!turnstileContainer) {
      this.formError = "Security feature container missing. Please refresh.";
      this.isRenderingTurnstile = false;
      return false;
    }

    return new Promise((resolvePromise) => {
      this.ngZone.runOutsideAngular(() => {
        try {
          this.turnstileWidgetId = window.turnstile!.render(
            turnstileContainer,
            {
              sitekey: this.turnstileSiteKey,
              execution: "execute",
              callback: (token: string) => {
                if (this.turnstileTokenResolve)
                  this.turnstileTokenResolve(token);
                this.turnstileTokenResolve = null;
              },
              "error-callback": () => {
                this.formError = "Security verification challenge failed.";
                if (this.turnstileTokenResolve)
                  this.turnstileTokenResolve(null);
                this.turnstileTokenResolve = null;
                this.isRenderingTurnstile = false;
                this.turnstileWidgetId = null;
                resolvePromise(false);
              },
              "expired-callback": () => {
                if (this.turnstileWidgetId && window.turnstile?.reset)
                  window.turnstile.reset(this.turnstileWidgetId);
                if (this.turnstileTokenResolve)
                  this.turnstileTokenResolve(null);
                this.turnstileTokenResolve = null;
                this.formError = "Security token expired. Please try again.";
                resolvePromise(false);
              },
              "timeout-callback": () => {
                if (this.turnstileWidgetId && window.turnstile?.reset)
                  window.turnstile.reset(this.turnstileWidgetId);
                if (this.turnstileTokenResolve)
                  this.turnstileTokenResolve(null);
                this.turnstileTokenResolve = null;
                this.formError =
                  "Security challenge timed out. Please try again.";
                resolvePromise(false);
              },
            }
          );

          this.ngZone.run(() => {
            if (this.turnstileWidgetId) {
              this.isRenderingTurnstile = false;
              resolvePromise(true);
            } else {
              this.formError =
                "Failed to initialize security feature (no widgetId).";
              this.isRenderingTurnstile = false;
              resolvePromise(false);
            }
          });
        } catch (renderError) {
          this.ngZone.run(() => {
            this.formError = "Critical error initializing security feature.";
            this.isRenderingTurnstile = false;
            resolvePromise(false);
          });
        }
      });
    });
  }

  private executeTurnstileAndGetToken(): Promise<string | null> {
    if (
      !this.turnstileWidgetId ||
      !window.turnstile?.reset ||
      !window.turnstile?.execute
    ) {
      this.formError =
        "Security feature not properly initialized. Please refresh.";
      return Promise.resolve(null);
    }

    if (this.turnstileTokenResolve) {
      this.turnstileTokenResolve(null);
    }
    this.turnstileTokenResolve = null;
    this.turnstileTokenPromise = null;

    // Reset Turnstile widget before execution
    window.turnstile.reset(this.turnstileWidgetId);

    let currentExecutionResolve: ((token: string | null) => void) | null = null;
    const currentExecutionPromise = new Promise<string | null>((resolve) => {
      currentExecutionResolve = resolve;
    });

    this.turnstileTokenResolve = currentExecutionResolve;

    // Execute Turnstile verification
    window.turnstile.execute(this.turnstileWidgetId);

    let timeoutId: any;
    const timeoutPromise = new Promise<null>((resolve) => {
      timeoutId = setTimeout(() => {
        resolve(null);
      }, 5000);
    });

    return Promise.race([currentExecutionPromise, timeoutPromise]).then(
      (token) => {
        clearTimeout(timeoutId);

        if (this.turnstileTokenResolve === currentExecutionResolve) {
          this.turnstileTokenResolve = null;
        }

        if (!token) {
          this.formError =
            this.formError ||
            "Security verification timed out. Please try again.";
        }
        return token;
      }
    );
  }

  toggleSignUpMode(): void {
    this.isSignUpMode = !this.isSignUpMode;
    this.errorMessage = null;
    this.successMessage = null;
    this.formError = "";
  }

  resetForms(): void {
    this.currentStep = 1;
    this.emailForm.reset();
    this.verificationForm.reset();
    this.errorMessage = null;
    this.successMessage = null;
    this.isSubmittingEmail = false;
    this.isVerifyingCode = false;
    this.codeSent = false;
    this.showVerificationForm = false;
    this.isSignUpMode = false;

    if (
      isPlatformBrowser(this.platformId) &&
      this.turnstileWidgetId &&
      window.turnstile?.reset
    ) {
      window.turnstile.reset(this.turnstileWidgetId);
    }
    if (this.turnstileTokenResolve) {
      this.turnstileTokenResolve(null);
      this.turnstileTokenResolve = null;
    }
  }

  closeModal(): void {
    this.resetForms();
    this.visibleChange.emit(false);
  }

  modalClick(event: Event): void {
    event.stopPropagation();
  }

  async signInWithEmail(isResend: boolean = false) {
    if (!isPlatformBrowser(this.platformId)) {
      this.formError = "Cannot sign in from this environment.";
      return;
    }

    if (isResend) {
    } else {
      if (this.isSubmitting) {
        return;
      }
    }

    if (!isResend) {
      this.isSubmitting = true;
    }

    this.formError = "";
    this.successMessage = null;
    this.turnstileToken = null;

    const widgetReady = await this.renderTurnstileWidgetIfNotExists();
    if (!widgetReady) {
      if (!isResend) this.isSubmitting = false;
      return;
    }

    try {
      const token = await this.executeTurnstileAndGetToken();

      if (!token) {
        if (!isResend) this.isSubmitting = false;
        return;
      }
      this.turnstileToken = token;

      // Proceed with Supabase OTP request
      const { data, error } = await this.authService.signInWithEmail(
        this.emailForm.value.email,
        token
      );

      if (error) {
        if (error.message && error.message.includes("Please wait a moment")) {
          this.formError = error.message;
          this.startResendCooldown();
          this.showResendCooldownOnButton = true;
        } else {
          throw error;
        }
        return;
      }

      this.lastOtpSendTimestamp = Date.now();
      if (!isResend) {
        this.showVerificationForm = true;
        this.codeSent = true;
        this.currentStep = 2;
        this.startResendCooldown();
      } else {
        this.startResendCooldown();
      }
    } catch (error: any) {
      this.formError =
        error.message || "Error processing your request. Please try again.";
      this.turnstileToken = null;
    } finally {
      if (!isResend) {
        this.isSubmitting = false;
      }
    }
  }

  async verifyCode(): Promise<void> {
    this.verificationForm.get("code")?.markAsTouched();
    if (this.verificationForm.invalid || this.isVerifyingCode) {
      return;
    }
    this.isVerifyingCode = true;
    this.errorMessage = null;
    this.formError = "";
    const email = this.emailForm.get("email")?.value;
    const code = this.verificationForm.get("code")?.value;

    try {
      const { data, error } = await this.authService.verifyOtp(email, code);
      if (error) {
        throw error;
      }
      this.successMessage = "Successfully verified!";
      if (data?.session) {
        this.loginComplete.emit(data.session.user);
        this.closeModal();
      }
    } catch (error: any) {
      const message = error.message?.toLowerCase() || "";
      if (message.includes("expired") || message.includes("token")) {
        this.errorMessage =
          "Verification code has expired. Please request a new one.";
      } else if (
        message.includes("invalid") ||
        message.includes("otp") ||
        message.includes("code")
      ) {
        this.errorMessage = "Invalid verification code. Please try again.";
      } else {
        this.errorMessage =
          error.message || "Unable to verify code. Please try again.";
      }
    } finally {
      this.isVerifyingCode = false;
    }
  }

  private resetTurnstileWidgetCompletely(): void {
    if (
      isPlatformBrowser(this.platformId) &&
      this.turnstileWidgetId &&
      window.turnstile?.remove
    ) {
      try {
        window.turnstile.remove(this.turnstileWidgetId);
      } catch (removeError) {
        // Error removing Turnstile widget
      }
    }
    this.turnstileWidgetId = null;
    this.isRenderingTurnstile = false;
    if (this.turnstileTokenResolve) {
      this.turnstileTokenResolve(null);
      this.turnstileTokenResolve = null;
    }
    this.turnstileTokenPromise = null;
    this.clearResendTimer();
  }

  resendCode(): void {
    const email = this.emailForm.get("email")?.value;
    if (!email) {
      this.formError = "Email is required to resend the verification code.";
      return;
    }

    if (this.isSubmitting) {
      this.formError =
        "Please wait for the current request to complete before resending.";
      return;
    }
    if (this.isResendingCode) {
      return;
    }

    if (this.resendCooldownActive) {
      this.showResendCooldownOnButton = true;
      return;
    }
    this.isResendingCode = true;
    this.showResendCooldownOnButton = false;
    this.errorMessage = null;
    this.successMessage = null;
    this.formError = "";
    this.verificationForm.get("code")?.reset();

    this.resetTurnstileWidgetCompletely();

    this.signInWithEmail(true)
      .then(() => {
        if (!this.formError) {
          this.successMessage = "New verification code sent! Check your email.";
        }
      })
      .catch((error) => {
        this.formError =
          this.formError || "Failed to resend code. Please try again.";
      })
      .finally(() => {
        this.isResendingCode = false;
      });
  }

  private startResendCooldown(): void {
    this.clearResendTimer(false);

    this.lastOtpSendTimestamp = Date.now();
    this.resendCooldownActive = true;
    this.showResendCooldownOnButton = false;
    let remaining = this.resendCooldownSeconds;
    this.resendTimerDisplay = `${remaining}s`;

    this.resendTimerInterval = setInterval(() => {
      remaining--;
      this.resendTimerDisplay = `${remaining}s`;
      if (remaining <= 0) {
        this.clearResendTimer();
      }
    }, 1000);
  }

  private clearResendTimer(log: boolean = true): void {
    if (this.resendTimerInterval) {
      clearInterval(this.resendTimerInterval);
      this.resendTimerInterval = null;
    }
    // Cooldown timer cleared
    this.resendCooldownActive = false;
    this.showResendCooldownOnButton = false;
    this.resendTimerDisplay = null;
  }

  ngOnDestroy(): void {
    this.clearResendTimer();
    if (
      this.turnstileWidgetId &&
      isPlatformBrowser(this.platformId) &&
      window.turnstile?.remove
    ) {
      try {
        window.turnstile.remove(this.turnstileWidgetId);
      } catch (e) {
        // Error removing turnstile widget
      }
    }
  }

  async signInWithGoogle(): Promise<void> {
    this.errorMessage = null;
    this.isSubmittingGoogle = true;
    try {
      this.authService.setRedirectPath(this.router.url);

      const { data, error } = await this.authService.signInWithProvider(
        "google"
      );
      if (error) {
        throw error;
      }
    } catch (error: any) {
      this.errorMessage =
        error.message || "Failed to sign in with Google. Please try again.";
      this.isSubmittingGoogle = false;
    }
  }
}
